import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="February 2026"
      seoTitle="Privacy Policy | PlanningSearch"
      seoDescription="How PlanningSearch collects, uses, and protects your personal data. UK planning application intelligence platform privacy policy."
      canonical="https://planningsearch.com/privacy"
    >
      <h2>1. Introduction</h2>
      <p>
        PlanningSearch Ltd ("we", "us", or "our") is committed to protecting the privacy of our users. This Privacy
        Policy explains how we collect, use, store, and share your personal information when you use the PlanningSearch
        website and services.
      </p>
      <p>
        We are the data controller for the purposes of the UK General Data Protection Regulation (UK GDPR) and the
        Data Protection Act 2018.
      </p>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Information you provide</h3>
      <ul>
        <li><strong>Account information:</strong> name, email address, and password when you register</li>
        <li><strong>Billing information:</strong> payment details processed securely via Stripe (we do not store card numbers)</li>
        <li><strong>Letterhead details:</strong> company name, address, phone, website, and logo when you set up letter profiles</li>
        <li><strong>Correspondence:</strong> letter content you create or generate using our platform</li>
        <li><strong>Communications:</strong> emails or messages you send to us</li>
      </ul>

      <h3>2.2 Information collected automatically</h3>
      <ul>
        <li><strong>Usage data:</strong> pages visited, search queries, features used, and time spent on the Service</li>
        <li><strong>Device data:</strong> browser type, operating system, screen resolution, and IP address</li>
        <li><strong>Cookies:</strong> we use essential cookies for authentication and functional cookies to remember your preferences</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your personal information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Process subscriptions and payments</li>
        <li>Send email alerts about new planning applications matching your preferences</li>
        <li>Generate and dispatch physical letters on your behalf</li>
        <li>Provide AI-generated intelligence reports and recommendations</li>
        <li>Respond to your enquiries and provide customer support</li>
        <li>Detect and prevent fraud, abuse, and security incidents</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4. Legal Basis for Processing</h2>
      <p>We process your personal data on the following legal bases:</p>
      <ul>
        <li><strong>Contract performance:</strong> to deliver the Service you have subscribed to</li>
        <li><strong>Legitimate interests:</strong> to improve our Service, prevent fraud, and communicate with you</li>
        <li><strong>Consent:</strong> for marketing communications and email alerts (you can opt out at any time)</li>
        <li><strong>Legal obligation:</strong> to comply with applicable laws and regulations</li>
      </ul>

      <h2>5. Data Sharing</h2>
      <p>We do not sell your personal data. We may share information with:</p>
      <ul>
        <li><strong>Payment processors:</strong> Stripe, for secure payment handling</li>
        <li><strong>Postal services:</strong> Pingen (or similar providers), to fulfil letter-sending requests</li>
        <li><strong>AI providers:</strong> Anthropic (Claude), for generating intelligence reports and letter content — only anonymised application data is shared</li>
        <li><strong>Hosting providers:</strong> for infrastructure and data storage</li>
        <li><strong>Law enforcement:</strong> when required by law or to protect our legal rights</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>
        We retain your account data for as long as your account is active. If you delete your account, we will
        remove your personal data within 30 days, except where we are required to retain it for legal or regulatory
        purposes.
      </p>
      <p>
        Email alert subscriptions without an account are retained until you unsubscribe.
      </p>

      <h2>7. Your Rights</h2>
      <p>Under UK data protection law, you have the right to:</p>
      <ul>
        <li><strong>Access:</strong> request a copy of the personal data we hold about you</li>
        <li><strong>Rectification:</strong> request correction of inaccurate or incomplete data</li>
        <li><strong>Erasure:</strong> request deletion of your personal data</li>
        <li><strong>Restriction:</strong> request that we limit processing of your data</li>
        <li><strong>Portability:</strong> receive your data in a structured, machine-readable format</li>
        <li><strong>Object:</strong> object to processing based on legitimate interests or for marketing</li>
        <li><strong>Withdraw consent:</strong> where processing is based on consent, you can withdraw it at any time</li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{' '}
        <a href="mailto:privacy@planningsearch.com">privacy@planningsearch.com</a>.
      </p>

      <h2>8. Cookies</h2>
      <p>
        We use essential cookies for authentication (JWT session tokens) and to remember your login state. We do
        not use advertising or tracking cookies. You can manage cookies through your browser settings.
      </p>

      <h2>9. Security</h2>
      <p>
        We implement appropriate technical and organisational measures to protect your personal data, including
        encrypted connections (HTTPS), hashed passwords (bcrypt), and secure authentication tokens. However, no
        method of transmission over the internet is 100% secure.
      </p>

      <h2>10. International Transfers</h2>
      <p>
        Your data is primarily stored and processed within the United Kingdom and European Economic Area. Where
        data is transferred to third-party providers outside the UK/EEA, we ensure appropriate safeguards are in
        place (e.g., Standard Contractual Clauses).
      </p>

      <h2>11. Children</h2>
      <p>
        The Service is not intended for individuals under the age of 18. We do not knowingly collect personal data
        from children.
      </p>

      <h2>12. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify registered users of material changes
        via email. The "Last updated" date at the top of this page indicates when the policy was last revised.
      </p>

      <h2>13. Contact</h2>
      <p>
        If you have questions or concerns about this Privacy Policy, please contact our Data Protection Officer at{' '}
        <a href="mailto:privacy@planningsearch.com">privacy@planningsearch.com</a>.
      </p>
    </LegalLayout>
  );
}
